import { useMemo, useState } from "react";
import { TbChevronDown, TbSearch } from "react-icons/tb";
import { CurrencyIcon } from "../../../../Components/Atoms/CurrencyIcon";
import { getAsset } from "../constants";

const TRIGGER_CLASS =
  "valut-input flex h-11 w-full min-w-0 items-center justify-between gap-2 rounded-xl !pr-10 text-left text-sm";

const SEARCH_THRESHOLD = 6;

const NetworkBadge = ({ label }) => (
  <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full bg-gray-100 text-[9px] font-bold uppercase text-gray-600">
    {label.slice(0, 2)}
  </span>
);

const formatAssetOption = (asset) => (
  <>
    <CurrencyIcon asset={asset.id} />
    <span className="min-w-0 truncate font-semibold text-brand-dark">{asset.label}</span>
  </>
);

const formatNetworkOption = (network) => (
  <>
    <NetworkBadge label={network.label} />
    <span className="min-w-0 truncate font-semibold text-brand-dark">{network.label}</span>
  </>
);

const StablesDropdown = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  id,
  className = "",
  formatOption,
  getSelected,
  searchPlaceholder,
  emptyMessage,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selected = getSelected(value);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term) ||
        option.id.toLowerCase().includes(term)
    );
  }, [options, search]);

  const showSearch = options.length >= SEARCH_THRESHOLD;

  const handleSelect = (optionId) => {
    if (disabled) return;
    onChange(optionId);
    setOpen(false);
    setSearch("");
  };

  const handleClose = () => {
    setOpen(false);
    setSearch("");
  };

  const handleToggle = () => {
    if (disabled) return;
    setOpen((prev) => !prev);
  };

  return (
    <div className={className}>
      {label && (
        <label htmlFor={id} className="mb-1.5 block text-xs font-medium text-gray-500">
          {label}
        </label>
      )}

      <div className="relative">
        <button
          id={id}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-disabled={disabled}
          disabled={disabled}
          onClick={handleToggle}
          className={`${TRIGGER_CLASS} ${selected ? "text-brand-dark" : "text-gray-400"} ${
            disabled ? "cursor-not-allowed opacity-60" : ""
          }`}
        >
          <span className="flex min-w-0 flex-1 items-center gap-2">
            {selected ? formatOption(selected) : placeholder}
          </span>
        </button>
        <TbChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          } ${disabled ? "opacity-50" : ""}`}
        />

        {open && !disabled && (
          <>
            <div className="fixed inset-0 z-10" onClick={handleClose} aria-hidden="true" />
            <div
              role="listbox"
              className="absolute left-0 right-0 z-20 mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-card dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:shadow-none"
            >
              {showSearch && (
                <div className="border-b border-gray-100 p-2 dark:border-brand-dark-border">
                  <div className="relative">
                    <TbSearch className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder={searchPlaceholder}
                      className="valut-input h-9 w-full rounded-lg pl-9 pr-3 text-sm"
                      autoFocus
                    />
                  </div>
                </div>
              )}

              <div className="max-h-60 overflow-y-auto py-1">
                {filteredOptions.length === 0 ? (
                  <p className="px-3 py-4 text-center text-xs text-gray-400">{emptyMessage}</p>
                ) : (
                  filteredOptions.map((option) => {
                    const isActive = option.id === value;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onClick={() => handleSelect(option.id)}
                        className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors ${
                          isActive
                            ? "bg-[#fff8ec] text-brand-dark dark:bg-brand-dark-surface dark:text-gray-100"
                            : "text-brand-dark hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-[#1f1f1f]"
                        }`}
                      >
                        {formatOption(option)}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export const StablesAssetSelect = ({
  value,
  onChange,
  options = [],
  exclude,
  label = "Ativo",
  placeholder = "Selecione o ativo",
  id = "stables-asset-select",
  className = "",
  disabled = false,
}) => {
  const availableOptions = useMemo(
    () => options.filter((asset) => asset.id !== exclude),
    [options, exclude]
  );

  const getSelected = (assetId) =>
    availableOptions.find((asset) => asset.id === assetId) ?? getAsset(assetId);

  return (
    <StablesDropdown
      label={label}
      value={value}
      onChange={onChange}
      options={availableOptions}
      placeholder={placeholder}
      id={id}
      className={className}
      formatOption={formatAssetOption}
      getSelected={getSelected}
      searchPlaceholder="Buscar ativo..."
      emptyMessage="Nenhum ativo encontrado."
      disabled={disabled}
    />
  );
};

export const StablesNetworkSelect = ({
  value,
  onChange,
  options = [],
  label = "Rede",
  placeholder = "Selecione a rede",
  id = "stables-network-select",
  className = "",
  disabled = false,
}) => {
  const getSelected = (networkId) => options.find((network) => network.id === networkId);

  return (
    <StablesDropdown
      label={label}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder}
      id={id}
      className={className}
      formatOption={formatNetworkOption}
      getSelected={getSelected}
      searchPlaceholder="Buscar rede..."
      emptyMessage="Nenhuma rede encontrada."
      disabled={disabled}
    />
  );
};
